import { FC, useEffect, useState } from 'react';

import { TWalletType, useWallets } from '@entities/wallets';
import type {
  IWallet,
  IWalletClickhouse,
  IWalletClickhouseTableColumns,
  IWalletClickhouseTableColumnsWithdrawal,
} from '@entities/wallets';
import { useRefetchAuto } from '@features/refetch-auto';
import ErrorMessage from '@shared/components/ErrorMessage';
import Loading from '@shared/components/Loading';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { Pagination, PaginationRows } from '@shared/components/Pagination';
import { Table, THead, TBody, TableContainer, TableProvider } from '@shared/components/Table';

import { BulkActions } from './settings/BulkActions';
import { useTableSettingsClickhouse } from './settings/useClickhouseTableSettings';

import './monitoring.css';

interface IProps {
  type: TWalletType;
}

const WalletsWidgetClickhouse: FC<IProps> = ({ type }) => {
  const { refreshInterval, setRefreshInterval } = useRefetchAuto({
    storageKey: `refetch-auto-wallet-${type}`,
  });

  const [freshWallets, setFreshWallets] = useState<IWallet[]>([]);

  const { wallets, isLoading, isError, error, isFetching, refetch, filter } = useWallets({
    type,
    refreshInterval,
    isClickhouse: true,
  });

  const {
    columnTitles,
    renderFilterElement,
    renderRow,
    renderItem,
    selectedWalletIds,
    clearSelection,
  } = useTableSettingsClickhouse({
    type,
    refetch,
    refreshInterval,
    setRefreshInterval,
    wallets: (wallets?.data as unknown as IWalletClickhouse[]) ?? [],
  });

  useEffect(() => {
    if (wallets?.data) {
      setFreshWallets(wallets.data);
    }
  }, [wallets]);

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorMessage error={error} />;

  if (wallets) {
    return (
      <>
        {isFetching && <Loading />}

        <TableProvider<IWalletClickhouseTableColumns | IWalletClickhouseTableColumnsWithdrawal>
          columnTitles={columnTitles}
          {...filter}
        >
          <TableContainer className='wallets-table table-docked-controls'>
            <Table>
              <THead renderItem={renderFilterElement} />
              <TBody
                items={wallets.data as unknown as IWalletClickhouse[]}
                renderItem={renderItem}
                renderRow={renderRow}
              />
            </Table>
          </TableContainer>
          <div className='pagination-container'>
            <Pagination data={wallets.meta} />
            <PaginationRows />
          </div>
        </TableProvider>
        <BulkActions
          wallets={freshWallets
            .filter(wallet => selectedWalletIds.includes(wallet.id))
            .flatMap(wallet => wallet.childs)}
          selectedCount={selectedWalletIds.length}
          selectedIds={selectedWalletIds}
          onClear={clearSelection}
        />
      </>
    );
  }

  return null;
};

export default WalletsWidgetClickhouse;
